import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { myProfile, MyProfile } from 'api/auth/user-profile';

export type ActionType = 'view' | 'add' | 'edit' | 'delete' | 'export' | 'import';

export type PageName =
  | 'cv'
  | 'company'
  | 'job'
  | 'user'
  | 'role'
  | 'doc'
  | 'chat'
  | 'status_cv1'
  | 'status_cv2'
  | 'data_private1'
  | 'data_private2'
  | 'data_private3';

export const userPermissionSubject = new BehaviorSubject<MyProfile | undefined>(undefined);

export const userSessionSubject = new BehaviorSubject<MyProfile | undefined>(undefined);

type PermRes<P> = P extends PageName ? boolean : Array<string>;

interface UserRight {
  cv: { view: number; add: number; edit: number; delete: number; export: number; import: number };
  company: { view: number; add: number; edit: number; delete: number; export: number; import: number };
  job: { view: number; add: number; edit: number; delete: number; export: number; import: number };
  user: { view: number; add: number; edit: number; delete: number; export: number; import: number };
  role: { view: number; add: number; edit: number; delete: number; export: number; import: number };
  doc: { view: number; add: number; edit: number; delete: number; export: number; import: number };
  chat: { view: number; add: number; edit: number; delete: number; export: number; import: number };
  status_cv1: { view: number; add: number; edit: number; delete: number; export: number; import: number };
  status_cv2: { view: number; add: number; edit: number; delete: number; export: number; import: number };
  data_private1: { view: number; add: number; edit: number; delete: number; export: number; import: number };
  data_private2: { view: number; add: number; edit: number; delete: number; export: number; import: number };
  data_private3: { view: number; add: number; edit: number; delete: number; export: number; import: number };
}

interface UserProfile {
  role: number;
  roleName: string;
}

class UserPermission {
  private userRight;

  private userProfile: UserProfile;

  private userRightKeys: Array<string>;

  private mapPageData: Array<string>;

  constructor() {
    userPermissionSubject.subscribe((data) => {
      this.userRight = data;
    });

    userSessionSubject.subscribe((data) => {
      if (!data) return;

      this.userProfile = {
        role: data.role,
        roleName: data.roleName,
      };
    });
  }

  private getPages(action: ActionType): PermRes<unknown> {
    let pages = [];

    if (!this.userRight) {
      return pages;
    }

    const keys = Object.keys(this.userRight);

    for (const key of keys) {
      if (this.userRight[key][action]) {
        pages.push(key);
      }
    }

    return pages;
  }

  private getPagesData<K extends ActionType, P extends PageName | unknown>(action: K, page?: P): PermRes<P> {
    if (page) {
      return this.getPages(action).includes(page as string) as PermRes<P>;
    }

    return this.getPages(action) as PermRes<P>;
  }

  public getUserRight(): UserRight {
    return this.userRight || {};
  }

  public canView<P extends PageName | unknown>(page?: P): PermRes<P> {
    return this.getPagesData('view', page);
  }

  public canAdd<P extends PageName | unknown>(page?: P): PermRes<P> {
    return this.getPagesData('add', page);
  }

  public canEdit<P extends PageName | unknown>(page?: P): PermRes<P> {
    return this.getPagesData('edit', page);
  }

  public canDelete<P extends PageName | unknown>(page?: P): PermRes<P> {
    return this.getPagesData('delete', page);
  }

  public canExport<P extends PageName | unknown>(page?: P): PermRes<P> {
    return this.getPagesData('export', page);
  }

  public canImport<P extends PageName | unknown>(page?: P): PermRes<P> {
    return this.getPagesData('import', page);
  }

  public async getMapPage(): Promise<Array<string>> {
    if (!this.userRight) {
      const profile = await lastValueFrom(myProfile());

      this.userRight = JSON.parse(profile.data.userRights);

      userSessionSubject.next(profile.data);
    }

    // Cache data
    if (this.mapPageData) {
      return this.mapPageData;
    }

    const userRight: UserRight = this.userRight;

    const keys = Object.keys(userRight);

    const actions = ['add', 'delete', 'edit', 'export', 'view', 'import'];

    const mapPage = keys.reduce((prev, curr) => {
      let perms = [];

      for (const action of actions) {
        if (userRight[curr][action]) {
          perms = [...perms, `/${curr}/${action}`];
        }
      }

      return [...prev, ...perms];
    }, []);

    this.userRightKeys = keys;
    this.mapPageData = mapPage;

    return this.mapPageData;
  }

  // Sure call getMapPage func before
  public getUserRightKeys(): Array<string> {
    return this.userRightKeys;
  }

  public getUserProfile(): UserProfile {
    return this.userProfile;
  }
}

export const userPermission = new UserPermission();
